import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get("category") || "";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "recent";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    api.get(`/products?${params.toString()}`).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [category, q, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v); else next.delete(k);
    setSearchParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-orange-600 mb-2">Catalog</div>
        <h1 data-testid="products-heading" className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight">
          {q ? `Results for "${q}"` : category ? categories.find((c) => c.slug === category)?.name || "Products" : "All Products"}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{products.length} items · Factory pricing</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="col-span-12 md:col-span-3 space-y-6">
          <div>
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mb-3">Categories</div>
            <ul className="space-y-1">
              <li>
                <button
                  data-testid="filter-all"
                  onClick={() => setParam("category", "")}
                  className={`text-sm py-1.5 hover:text-orange-600 ${!category ? "text-orange-600 font-bold" : "text-slate-700"}`}
                >
                  All Products
                </button>
              </li>
              {categories.map((c) => (
                <li key={c.slug}>
                  <button
                    data-testid={`filter-${c.slug}`}
                    onClick={() => setParam("category", c.slug)}
                    className={`text-sm py-1.5 hover:text-orange-600 text-left ${category === c.slug ? "text-orange-600 font-bold" : "text-slate-700"}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Grid */}
        <div className="col-span-12 md:col-span-9">
          <div className="flex items-center justify-between mb-6">
            <div className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">{products.length} products</div>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger data-testid="sort-select" className="rounded-none w-48 border-slate-300"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="price_asc">Price: Low → High</SelectItem>
                <SelectItem value="price_desc">Price: High → Low</SelectItem>
                <SelectItem value="name">Name A → Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array(6).fill(null).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 border border-slate-200">
              <div className="font-display font-bold uppercase tracking-tight text-xl">No products found</div>
              <Link to="/products" className="text-orange-600 mt-4 inline-block text-sm">← Browse all products</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
